import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { authService, type AuthService, type AuthSignUpResult } from '../services/authService';
import type { InviteCodeHash } from '../utils/inviteCode';
import { databaseManager } from '../database/databaseManager';
import {
  getAvatarReference,
  profileService,
  ProfileService,
  validateDisplayName
} from '../services/profileService';

interface AuthContextValue {
  avatarUrl: string | null;
  configurationMessage: string;
  error: string | null;
  isAuthenticated: boolean;
  isConfigured: boolean;
  isLoading: boolean;
  isAvatarLoading: boolean;
  isPasswordRecovery: boolean;
  requestPasswordReset(email: string): Promise<void>;
  removeAvatar(): Promise<void>;
  session: Session | null;
  signIn(email: string, password: string): Promise<Session>;
  signOut(): Promise<void>;
  signUp(email: string, password: string, inviteCodeHash: InviteCodeHash, displayName: string): Promise<AuthSignUpResult>;
  updatePassword(password: string): Promise<void>;
  updateAvatar(file: File): Promise<void>;
  updateDisplayName(displayName: string): Promise<void>;
  user: User | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({
  children,
  service = authService,
  profiles = profileService
}: PropsWithChildren<{ service?: AuthService; profiles?: ProfileService }>) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(service.isConfigured);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarState, setAvatarState] = useState<{ blob: Blob; userId: string; version: string }>();
  const [avatarObjectUrl, setAvatarObjectUrl] = useState<{ url: string; userId: string; version: string }>();
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);

  useEffect(() => {
    if (!service.isConfigured) return;

    let active = true;
    let observedAuthState = false;

    let unsubscribe: () => void = () => undefined;
    try {
      unsubscribe = service.onAuthStateChange((event, nextSession) => {
        if (!active) return;
        observedAuthState = true;
        setSession(nextSession);
        setError(null);
        setIsLoading(false);
        if (event === 'PASSWORD_RECOVERY') setIsPasswordRecovery(true);
        if (event === 'SIGNED_OUT') setIsPasswordRecovery(false);
      });
    } catch (initializationError) {
      queueMicrotask(() => {
        if (!active) return;
        setError(initializationError instanceof Error ? initializationError.message : 'Não foi possível observar a sessão da conta.');
        setIsLoading(false);
      });
    }

    void service.getSession()
      .then((currentSession) => {
        if (!active || observedAuthState) return;
        setSession(currentSession);
        setIsLoading(false);
      })
      .catch((sessionError: unknown) => {
        if (!active || observedAuthState) return;
        setError(sessionError instanceof Error ? sessionError.message : 'Não foi possível consultar a sessão da conta.');
        setIsLoading(false);
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [service]);

  const user = session?.user ?? null;
  const userId = user?.id;
  const avatarReference = useMemo(() => getAvatarReference(user), [user]);
  const avatarVersion = avatarReference?.version;

  useEffect(() => {
    if (!userId) return;
    let active = true;
    let refreshing = false;

    const refreshCurrentUser = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const freshUser = await service.getCurrentUser();
        if (!active) return;
        setSession((current) => {
          if (!current || current.user.id !== freshUser.id) return current;
          const currentMetadata = current.user.user_metadata ?? {};
          const freshMetadata = freshUser.user_metadata ?? {};
          const profileChanged = ['display_name', 'avatar_path', 'avatar_version']
            .some((key) => currentMetadata[key] !== freshMetadata[key]);
          return profileChanged ? { ...current, user: freshUser } : current;
        });
      } catch {
        // Falhas transitórias não alteram sessão, banco local nem fluxo de autenticação.
      } finally {
        refreshing = false;
      }
    };
    const refreshWhenOnline = () => { void refreshCurrentUser(); };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refreshCurrentUser();
    };
    window.addEventListener('online', refreshWhenOnline);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      active = false;
      window.removeEventListener('online', refreshWhenOnline);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [service, userId]);

  useEffect(() => {
    if (!user || !avatarReference) {
      setAvatarState(undefined);
      setIsAvatarLoading(false);
      return;
    }
    setAvatarState((current) => current?.userId === user.id && current.version === avatarReference.version
      ? current
      : undefined);
    let active = true;
    setIsAvatarLoading(true);
    void (async () => {
      try {
        const cached = await profiles.getCachedAvatar(user);
        if (active && cached) setAvatarState({ blob: cached, userId: user.id, version: avatarReference.version });
      } catch {
        // Falha da cache não impede uma tentativa autenticada de atualização remota.
      }
      if (!active || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
      try {
        const fresh = await profiles.refreshAvatar(user);
        if (active && fresh) setAvatarState({ blob: fresh, userId: user.id, version: avatarReference.version });
      } catch {
        // O cache ou o fallback de inicial permanecem válidos durante falhas de rede.
      }
    })().finally(() => {
      if (active) setIsAvatarLoading(false);
    });
    return () => { active = false; };
  }, [avatarReference, profiles, user]);

  const activeAvatarBlob = avatarState?.userId === user?.id && avatarState?.version === avatarVersion
    ? avatarState?.blob
    : undefined;

  useEffect(() => {
    if (!activeAvatarBlob || !user || !avatarVersion || typeof URL.createObjectURL !== 'function') {
      setAvatarObjectUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(activeAvatarBlob);
    setAvatarObjectUrl({ url, userId: user.id, version: avatarVersion });
    return () => URL.revokeObjectURL(url);
  }, [activeAvatarBlob, avatarVersion, user]);

  const avatarUrl = avatarObjectUrl && user
    && avatarObjectUrl.userId === user.id && avatarObjectUrl.version === avatarVersion
    ? avatarObjectUrl.url
    : null;

  const signUp = useCallback(async (email: string, password: string, inviteCodeHash: InviteCodeHash, displayName: string) => {
    await databaseManager.assertCanLeaveActiveDatabase();
    const result = await service.signUp(email, password, inviteCodeHash, displayName);
    if (result.session) setSession(result.session);
    return result;
  }, [service]);
  const signIn = useCallback(async (email: string, password: string) => {
    await databaseManager.assertCanLeaveActiveDatabase();
    const nextSession = await service.signIn(email, password);
    setSession(nextSession);
    return nextSession;
  }, [service]);
  const signOut = useCallback(async () => {
    await databaseManager.assertCanSwitchTo(null);
    await service.signOut();
    setSession(null);
    setIsPasswordRecovery(false);
  }, [service]);
  const requestPasswordReset = useCallback((email: string) => service.requestPasswordReset(email), [service]);
  const updatePassword = useCallback(async (password: string) => {
    await service.updatePassword(password);
    setIsPasswordRecovery(false);
  }, [service]);
  const replaceSessionUser = useCallback((nextUser: User) => {
    setSession((current) => current ? { ...current, user: nextUser } : current);
  }, []);
  const updateDisplayName = useCallback(async (displayName: string) => {
    const currentUser = session?.user;
    if (!currentUser) throw new Error('Sessão autenticada necessária.');
    const nextUser = await service.updateUserMetadata({
      display_name: validateDisplayName(displayName)
    });
    replaceSessionUser(nextUser);
  }, [replaceSessionUser, service, session?.user]);
  const updateAvatar = useCallback(async (file: File) => {
    const currentUser = session?.user;
    if (!currentUser) throw new Error('Sessão autenticada necessária.');
    const result = await profiles.replaceAvatar(
      currentUser,
      file,
      (patch) => service.updateUserMetadata(patch),
      () => service.getCurrentUser()
    );
    replaceSessionUser(result.user);
    const reference = getAvatarReference(result.user);
    if (reference) setAvatarState({ blob: result.blob, userId: result.user.id, version: reference.version });
  }, [profiles, replaceSessionUser, service, session?.user]);
  const removeAvatar = useCallback(async () => {
    const currentUser = session?.user;
    if (!currentUser) throw new Error('Sessão autenticada necessária.');
    const nextUser = await profiles.removeAvatar(
      currentUser,
      (patch) => service.updateUserMetadata(patch)
    );
    replaceSessionUser(nextUser);
    setAvatarState(undefined);
  }, [profiles, replaceSessionUser, service, session?.user]);

  const value = useMemo<AuthContextValue>(() => ({
    avatarUrl,
    configurationMessage: service.configurationMessage,
    error,
    isAuthenticated: session !== null,
    isConfigured: service.isConfigured,
    isLoading,
    isAvatarLoading,
    isPasswordRecovery,
    requestPasswordReset,
    removeAvatar,
    session,
    signIn,
    signOut,
    signUp,
    updatePassword,
    updateAvatar,
    updateDisplayName,
    user
  }), [avatarUrl, error, isAvatarLoading, isLoading, isPasswordRecovery, removeAvatar, requestPasswordReset, service.configurationMessage, service.isConfigured, session, signIn, signOut, signUp, updateAvatar, updateDisplayName, updatePassword, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// O hook acompanha o provider no mesmo módulo para manter uma única API pública de autenticação.
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth deve ser usado dentro de AuthProvider.');
  return value;
};
